import { Document } from '../types';

// 网页版文档管理器，增强了离线功能和同步能力
export class DocumentManager {
  private static readonly STORAGE_KEY = 'daily-notes-documents';
  private static readonly SYNC_QUEUE_KEY = 'daily-notes-sync-queue';
  private static readonly LAST_SYNC_KEY = 'daily-notes-last-sync';

  // 获取所有文档
  static getAllDocuments(): Document[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];

      const documents = JSON.parse(data);
      return documents.map((doc: any) => ({
        ...doc,
        createdAt: new Date(doc.createdAt),
        updatedAt: new Date(doc.updatedAt),
      }));
    } catch (error) {
      return [];
    }
  }

  // 创建新文档
  static createDocument(title: string, content: string, category: string): Document {
    const newDocument: Document = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      title,
      content,
      category,
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.saveDocument(newDocument);
    return newDocument;
  }

  // 保存文档
  static saveDocument(document: Document): void {
    try {
      const documents = this.getAllDocuments();
      const existingIndex = documents.findIndex(doc => doc.id === document.id);

      const updatedDocument = {
        ...document,
        updatedAt: new Date(),
      };

      if (existingIndex !== -1) {
        documents[existingIndex] = updatedDocument;
      } else {
        documents.push(updatedDocument);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(documents));

      // 网页版特有：添加到同步队列
      this.addToSyncQueue('save', updatedDocument);

      // 触发文档变化事件
      this.triggerDocumentChange('save', updatedDocument);
    } catch (error) {
      throw error;
    }
  }

  // 删除文档
  static deleteDocument(id: string): void {
    try {
      const documents = this.getAllDocuments();
      const documentToDelete = documents.find(doc => doc.id === id);
      const filteredDocuments = documents.filter(doc => doc.id !== id);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredDocuments));

      // 网页版特有：添加到同步队列
      if (documentToDelete) {
        this.addToSyncQueue('delete', documentToDelete);
      }

      // 触发文档变化事件
      this.triggerDocumentChange('delete', documentToDelete);
    } catch (error) {
      throw error;
    }
  }

  // 根据分类获取文档
  static getDocumentsByCategory(category: string): Document[] {
    const documents = this.getAllDocuments();
    return documents.filter(doc => doc.category === category);
  }

  // 获取所有分类
  static getAllCategories(): string[] {
    const documents = this.getAllDocuments();
    const categories = new Set(documents.map(doc => doc.category));
    return Array.from(categories);
  }

  // 搜索文档
  static searchDocuments(query: string): Document[] {
    const documents = this.getAllDocuments();
    const lowerQuery = query.toLowerCase();

    return documents.filter(doc =>
      doc.title.toLowerCase().includes(lowerQuery) ||
      doc.content.toLowerCase().includes(lowerQuery) ||
      doc.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  }

  // 网页版特有功能

  // 添加到同步队列
  private static addToSyncQueue(action: 'save' | 'delete', document: Document): void {
    try {
      const queue = this.getSyncQueue();
      queue.push({
        id: Date.now().toString(),
        action,
        document,
        timestamp: Date.now(),
        retryCount: 0
      });

      localStorage.setItem(this.SYNC_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      // 静默处理同步队列错误
    }
  }

  // 获取同步队列
  static getSyncQueue(): Array<{
    id: string;
    action: 'save' | 'delete';
    document: Document;
    timestamp: number;
    retryCount: number;
  }> {
    try {
      const data = localStorage.getItem(this.SYNC_QUEUE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  // 清空同步队列
  static clearSyncQueue(): void {
    localStorage.removeItem(this.SYNC_QUEUE_KEY);
  }

  // 触发文档变化事件
  private static triggerDocumentChange(action: string, document?: Document): void {
    window.dispatchEvent(new CustomEvent('documentChanged', {
      detail: { action, document }
    }));
  }

  // 导出文档
  static exportDocuments(format: 'json' | 'markdown' = 'json'): Blob {
    const documents = this.getAllDocuments();

    if (format === 'json') {
      const jsonData = JSON.stringify(documents, null, 2);
      return new Blob([jsonData], { type: 'application/json' });
    } else {
      const markdownData = documents.map(doc =>
        `# ${doc.title}\n\n**分类**: ${doc.category}\n**创建时间**: ${doc.createdAt.toLocaleString()}\n**更新时间**: ${doc.updatedAt.toLocaleString()}\n\n${doc.content}\n\n---\n\n`
      ).join('');
      return new Blob([markdownData], { type: 'text/markdown' });
    }
  }

  // 导入文档
  static async importDocuments(file: File): Promise<number> {
    const text = await file.text();

    try {
      const importedDocs = JSON.parse(text);
      let importedCount = 0;

      for (const doc of importedDocs) {
        try {
          const newDoc: Document = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            title: doc.title || '未命名',
            content: doc.content || '',
            category: doc.category || '默认',
            tags: doc.tags || [],
            createdAt: new Date(doc.createdAt || Date.now()),
            updatedAt: new Date(doc.updatedAt || Date.now()),
          };

          this.saveDocument(newDoc);
          importedCount++;
        } catch (error) {
          // 静默处理单个文档导入失败
        }
      }

      return importedCount;
    } catch (error) {
      throw new Error('文件格式不正确');
    }
  }

  // 批量操作
  static batchUpdateDocuments(documentIds: string[], updates: Partial<Document>): void {
    const documents = this.getAllDocuments();
    let hasChanges = false;

    documents.forEach(doc => {
      if (documentIds.includes(doc.id)) {
        Object.assign(doc, updates, { updatedAt: new Date() });
        hasChanges = true;
      }
    });

    if (hasChanges) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(documents));
      this.triggerDocumentChange('batchUpdate');
    }
  }

  // 获取文档统计信息
  static getDocumentStats(): {
    total: number;
    categories: { [key: string]: number };
    recentCount: number;
    todayCount: number;
  } {
    const documents = this.getAllDocuments();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const categories: { [key: string]: number } = {};
    let recentCount = 0;
    let todayCount = 0;

    documents.forEach(doc => {
      // 统计分类
      categories[doc.category] = (categories[doc.category] || 0) + 1;

      // 统计最近一周
      if (doc.updatedAt >= weekAgo) {
        recentCount++;
      }

      // 统计今天
      if (doc.updatedAt >= today) {
        todayCount++;
      }
    });

    return {
      total: documents.length,
      categories,
      recentCount,
      todayCount
    };
  }

  // 清理过期数据
  static cleanupOldData(daysToKeep: number = 30): number {
    const cutoffDate = new Date(Date.now() - daysToKeep * 24 * 60 * 60 * 1000);
    const documents = this.getAllDocuments();
    const initialCount = documents.length;

    const filteredDocuments = documents.filter(doc =>
      doc.updatedAt >= cutoffDate || doc.createdAt >= cutoffDate
    );

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredDocuments));

    const removedCount = initialCount - filteredDocuments.length;
    if (removedCount > 0) {
      this.triggerDocumentChange('cleanup');
    }

    return removedCount;
  }

  // 获取存储使用情况
  static getStorageUsage(): {
    used: number;
    total: number;
    percentage: number;
  } {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY) || '';
      const used = new Blob([data]).size;

      // 估算localStorage总容量（通常为5-10MB）
      const total = 5 * 1024 * 1024; // 5MB
      const percentage = (used / total) * 100;

      return { used, total, percentage };
    } catch (error) {
      return { used: 0, total: 0, percentage: 0 };
    }
  }

  // 设置最后同步时间
  static setLastSyncTime(timestamp: number = Date.now()): void {
    localStorage.setItem(this.LAST_SYNC_KEY, timestamp.toString());
  }

  // 获取最后同步时间
  static getLastSyncTime(): number | null {
    const timestamp = localStorage.getItem(this.LAST_SYNC_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }
}
